/** Create/edit form for a Signal. Validated against `createSignalSchema`. */
import { useForm } from "@refinedev/react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  SIGNAL_TYPES,
  SIGNAL_STATUS,
  PACKAGE_TYPES,
  createSignalSchema,
  type CreateSignalInput,
} from "@aos/shared";

import { FormShell } from "../skills/_components/FormShell";
import {
  NumberField,
  optionsFromEnum,
  SelectField,
  TextAreaField,
  TextField,
} from "../skills/_components/form";

export interface SignalFormProps {
  action: "create" | "edit";
}

export function SignalForm({ action }: SignalFormProps) {
  const form = useForm<CreateSignalInput>({
    resolver: zodResolver(createSignalSchema),
    refineCoreProps: { resource: "signals", action },
  });

  const {
    refineCore: { onFinish, formLoading },
    handleSubmit,
  } = form;

  return (
    <FormShell
      title={action === "create" ? "New Signal" : "Edit Signal"}
      resource="signals"
      isSubmitting={formLoading}
      onSubmit={handleSubmit((values) => onFinish(values))}
    >
      <TextField
        form={form}
        name="title"
        label="Title"
        placeholder="Recurring question about invoice format"
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <SelectField
          form={form}
          name="signal_type"
          label="Signal Type"
          options={optionsFromEnum(SIGNAL_TYPES)}
        />
        <SelectField
          form={form}
          name="status"
          label="Status"
          options={optionsFromEnum(SIGNAL_STATUS)}
          allowEmpty
        />
      </div>
      <TextField
        form={form}
        name="context_id"
        label="Context ID"
        placeholder="cx_…"
        description="The context this signal originated in."
      />
      <TextAreaField
        form={form}
        name="body"
        label="Body"
        placeholder="Describe the signal…"
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextField
          form={form}
          name="source_type"
          label="Source Type"
          placeholder="user_request"
        />
        <TextField
          form={form}
          name="source_id"
          label="Source ID"
          placeholder="evt_… / art_…"
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <NumberField
          form={form}
          name="strength"
          label="Strength"
          placeholder="0.0 – 1.0"
        />
        <NumberField
          form={form}
          name="confidence_score"
          label="Confidence Score"
          placeholder="0.0 – 1.0"
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <SelectField
          form={form}
          name="candidate_package_type"
          label="Candidate Package Type"
          options={optionsFromEnum(PACKAGE_TYPES)}
          allowEmpty
        />
        <TextField
          form={form}
          name="candidate_package_id"
          label="Candidate Package ID"
          placeholder="pkg_…"
        />
      </div>
      <TextField
        form={form}
        name="created_by_user_id"
        label="Created By User ID"
        placeholder="user_…"
      />
    </FormShell>
  );
}

export default SignalForm;
